import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, ArrowUp, Loader2, X } from "lucide-react";

/**
 * The Sequence.
 *
 * A Cluster says what the work is about. A Stack says what comes before what —
 * so position IS the dependency. Step three cannot start until step two is
 * done, and that is stated on the page rather than implied by a drag handle.
 * MelzAI proposes the order; you overrule him and it is yours.
 */
export const StackPanel = ({ stack, textOf, busy, onMove, onSave, onClose, onMission, onDesk }) => (
  <motion.div
    className="glass-warm relative w-full max-w-2xl rounded-2xl p-7 md:p-9"
    data-testid="stack-panel"
    initial={{ opacity: 0, y: 24, scale: 0.97 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: 16, scale: 0.98 }}
    transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
  >
    <button
      type="button"
      onClick={onClose}
      data-testid="stack-close"
      className="absolute right-5 top-5 text-white/25 transition-colors hover:text-white/70"
      aria-label="close"
    >
      <X size={14} />
    </button>

    <div className="tracked mb-2 text-[9px] text-[var(--stage-gold)]/75">
      Isolated as a Stack · sequenced {stack.sequenced_by === "you" ? "by you" : "by MelzAI"}
    </div>
    <h2 className="serif text-3xl font-light leading-tight text-[#f7ecd8]">{stack.title}</h2>
    <p
      className="mt-3 max-w-lg text-[13px] italic leading-relaxed text-white/45"
      data-testid="sequence-rationale"
    >
      {stack.rationale}
    </p>

    <div className="mt-7 space-y-2" data-testid="sequence-list">
      {stack.ordered_shard_ids.map((sid, i) => (
        <motion.div
          key={sid}
          layout
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          className="group flex items-start gap-4 rounded-lg border border-white/8 bg-black/25 px-4 py-3"
          data-testid="stack-step"
        >
          <span className="mono w-5 shrink-0 pt-0.5 text-[13px] text-[var(--stage-gold)]/70">
            {i + 1}
          </span>
          <span className="flex-1 text-[13.5px] leading-snug text-white/70">{textOf[sid]}</span>
          <span className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              type="button"
              data-testid="step-up"
              disabled={i === 0}
              onClick={() => onMove(i, -1)}
              className="text-white/30 transition-colors hover:text-[var(--stage-gold)] disabled:opacity-15"
              aria-label="earlier"
            >
              <ArrowUp size={12} />
            </button>
            <button
              type="button"
              data-testid="step-down"
              disabled={i === stack.ordered_shard_ids.length - 1}
              onClick={() => onMove(i, 1)}
              className="text-white/30 transition-colors hover:text-[var(--stage-gold)] disabled:opacity-15"
              aria-label="later"
            >
              <ArrowDown size={12} />
            </button>
          </span>
        </motion.div>
      ))}
    </div>

    <p className="mono mt-4 text-[9px] leading-relaxed text-white/25">
      Position is the dependency. Step {stack.ordered_shard_ids.length} cannot start until
      step {Math.max(1, stack.ordered_shard_ids.length - 1)} is finished.
    </p>

    <div className="mt-7 flex flex-wrap items-center gap-4">
      <button
        type="button"
        data-testid="save-sequence-button"
        disabled={busy}
        onClick={onSave}
        className="btn btn-gold"
      >
        {busy ? <Loader2 className="animate-spin" size={14} /> : "This is the order"}
      </button>
      <button
        type="button"
        data-testid="take-to-desk-button"
        disabled={busy}
        onClick={onDesk}
        className="btn"
      >
        Take it to the Desk →
      </button>
      <button
        type="button"
        data-testid="set-mission-button"
        disabled={busy}
        onClick={onMission}
        className="mono text-[10px] text-white/35 transition-colors hover:text-[var(--stage-gold)]"
      >
        → make this the Mission
      </button>
    </div>
  </motion.div>
);

export default StackPanel;
